import { useEffect } from "react";

const SITE_NAME = "RSTI Global";
const BASE = (process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");
const CONTACT_EMAIL = "contact@rstiglobal.com";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href, hreflang) {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Sets title, description, canonical, hreflang FR/EN + x-default, OG tags and optional JSON-LD.
 */
export function useSeo({ title, description, lang = "fr", image, type = "website", jsonLd, robots = "index,follow" } = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Reveal · Structure · Transformer · Impact`;
    document.title = fullTitle;
    document.documentElement.lang = lang;

    if (description) upsertMeta("name", "description", description);
    upsertMeta("name", "robots", robots);

    const path = window.location.pathname.replace(/\/+$/, "") || "/";
    const canonical = `${BASE}${path}${lang === "fr" ? "" : `?lang=${lang}`}`;
    const indexable = !robots.includes("noindex");
    document.head.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]').forEach((el) => { if (!indexable) el.remove(); });
    if (indexable) {
      upsertLink("canonical", canonical);
      upsertLink("alternate", `${BASE}${path}?lang=fr`, "fr");
      upsertLink("alternate", `${BASE}${path}?lang=en`, "en");
      upsertLink("alternate", `${BASE}${path}`, "x-default");
    }

    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:site_name", SITE_NAME);
    if (image) upsertMeta("property", "og:image", image.startsWith("http") ? image : `${BASE}${image}`);
    upsertMeta("name", "twitter:card", image ? "summary_large_image" : "summary");

    let script = document.getElementById("seo-jsonld");
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = "seo-jsonld";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
  }, [title, description, lang, image, type, robots, JSON.stringify(jsonLd)]);
}

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${BASE}/#organization`,
        name: SITE_NAME,
        alternateName: "RSTI",
        slogan: "Reveal · Structure · Transformer · Impact",
        description: "Plateforme internationale opérée par RSTI, ancrée en Afrique : qualification, structuration et mise en relation de projets, partenaires, financements et marchés entre l'Afrique, l'Europe et l'Asie.",
        url: BASE,
        logo: `${BASE}/logo-rsti-512.png`,
        email: CONTACT_EMAIL,
        areaServed: ["Africa", "Europe", "Asia"],
      },
      {
        "@type": "WebSite",
        "@id": `${BASE}/#website`,
        url: BASE,
        name: SITE_NAME,
        publisher: { "@id": `${BASE}/#organization` },
        inLanguage: ["fr", "en"],
        potentialAction: {
          "@type": "SearchAction",
          target: { "@type": "EntryPoint", urlTemplate: `${BASE}/search?q={search_term_string}` },
          "query-input": "required name=search_term_string",
        },
      },
    ],
  };
}
